<template>
  <div class="login-page">
    <!-- 背景装饰 -->
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <h1 class="brand-name">树苗英语</h1>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id && item.id !== 'logout' }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
        </nav>
      </aside>

      <main class="category-main">
        <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
          <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="mobile-topbar-title">分类</h1>
          <span class="mobile-topbar-spacer" aria-hidden="true"></span>
        </div>
        <div class="mobile-topbar-offset" aria-hidden="true"></div>

        <!-- 选择卡片 -->
        <div class="cards-container">
          <div class="cards-column">
            <!-- 启蒙儿歌视频库 -->
            <div class="glass-card selection-card" @click="handleSelect('songs')" :class="{ active: selected === 'songs' }">
              <div class="card-inner">
                <span class="card-tag song-tag">节奏与听力</span>
                <h2 class="card-title">{{ songsCardTitle }}</h2>
                <p class="card-desc">经典SSS风格儿歌，磨耳朵的最佳伙伴。</p>
                <div class="card-icon song-icon">
                  <font-awesome-icon icon="music" />
                </div>
              </div>
              <div class="card-shine"></div>
            </div>

            <!-- 音频库 -->
            <div class="glass-card selection-card" @click="handleSelect('audio')" :class="{ active: selected === 'audio' }">
              <div class="card-inner">
                <span class="card-tag audio-tag">陪伴磨耳朵</span>
                <h2 class="card-title">{{ audioCardTitle }}</h2>
                <p class="card-desc">精选轻听内容，适合通勤、睡前和碎片时间自然输入英语。</p>
                <div class="card-icon audio-icon">
                  <font-awesome-icon icon="headphones" />
                </div>
              </div>
              <div class="card-shine"></div>
            </div>

            <!-- TPR 词卡馆 -->
            <div
              v-if="!isAdvancedStage"
              class="glass-card selection-card"
              @click="handleSelect('tpr')"
              :class="{ active: selected === 'tpr' }"
            >
              <div class="card-inner">
                <span class="card-tag tpr-tag">感知与互动</span>
                <h2 class="card-title">TPR 互动卡</h2>
                <p class="card-desc">通过身体反应学习，日常指令与情绪感知一触即达。</p>
                <div class="card-icon tpr-icon">
                  <font-awesome-icon icon="hand-sparkles" />
                </div>
              </div>
              <div class="card-shine"></div>
            </div>

            <!-- 单词闪卡 -->
            <div
              v-if="showFlashCard"
              class="glass-card selection-card"
              @click="handleSelect('flash')"
              :class="{ active: selected === 'flash' }"
            >
              <div class="card-inner">
                <span class="card-tag flash-tag">词汇记忆</span>
                <h2 class="card-title">单词闪卡</h2>
                <p class="card-desc">用大图与发音快速认读核心单词，适合短时高频复习。</p>
                <div class="card-icon flash-icon">
                  <font-awesome-icon icon="book-open" />
                </div>
              </div>
              <div class="card-shine"></div>
            </div>

            <!-- 英文绘本 -->
            <div
              v-if="showPictureBookCard"
              class="glass-card selection-card"
              @click="handleSelect('picture')"
              :class="{ active: selected === 'picture' }"
            >
              <div class="card-inner">
                <span class="card-tag picture-tag">分级阅读</span>
                <h2 class="card-title">英文绘本</h2>
                <p class="card-desc">用短句故事进入阅读理解，适合进阶阶段。</p>
                <div class="card-icon picture-icon">
                  <font-awesome-icon icon="book-open" />
                </div>
              </div>
              <div class="card-shine"></div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <Transition name="toast-fade">
      <div v-if="toastMessage" class="toast-message">{{ toastMessage }}</div>
    </Transition>

    <ParentPasswordModal ref="parentPasswordModalRef" />
  </div>
</template>

<script setup>
import logo2Image from '../assets/logo2.png'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()
const AUTH_STORAGE_KEY = 'tree-sprout-auth'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const selected = ref(null)
const isTopbarScrolled = ref(false)
const toastMessage = ref('')
const activeNav = ref('')
const parentPasswordModalRef = ref(null)
let toastTimer = null
const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]

navItems.push({ id: 'logout', label: '登出账号', icon: 'right-from-bracket' })
const isAdvancedStage = computed(() => ['expression', 'ability'].includes(route.query.stage))
const isSenseStage = computed(() => route.query.stage === 'sense')
const showFlashCard = computed(() => !isSenseStage.value)
const showPictureBookCard = computed(() => isAdvancedStage.value)
const songsCardTitle = computed(() => {
  const titleMap = {
    sense: '启蒙儿歌视频库',
    sprout: '启蒙动画视频库',
    expression: '启蒙动画视频库',
    ability: '英文动画视频库'
  }

  return titleMap[route.query.stage] || '启蒙儿歌视频库'
})
const audioCardTitle = computed(() => {
  const titleMap = {
    sense: '启蒙儿歌音频库',
    sprout: '启蒙儿歌音频库',
    expression: '有声书音频库',
    ability: '有声书音频库'
  }

  return titleMap[route.query.stage] || '启蒙儿歌音频库'
})

const showToast = (message) => {
  toastMessage.value = message
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => {
    toastMessage.value = ''
  }, 1800)
}

const handleLogout = () => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(PARENT_VERIFIED_STORAGE_KEY)
  router.replace('/login')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'logout') {
    handleLogout()
    return
  }

  if (item.id === 'home') {
    router.push('/home')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const handleSelect = (type) => {
  selected.value = type
  // 根据选择跳转到对应页面
  if (type === 'tpr') {
    router.push('/tpr')
  } else if (type === 'flash') {
    router.push('/flash')
  } else if (type === 'songs') {
    router.push('/videos')
  } else if (type === 'audio') {
    router.push({
      path: '/audio',
      query: {
        title: audioCardTitle.value
      }
    })
  } else if (type === 'picture') {
    selected.value = null
    showToast('功能开发中，敬请期待')
  }
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/home')
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 12
}

onMounted(() => {
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.clearTimeout(toastTimer)
  window.removeEventListener('scroll', handlePageScroll)
})
</script>

<style scoped>
/* 基础重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 页面容器 */
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;
  overflow: hidden;
}

/* 背景渐变 */
.bg-gradient {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #FAF8F5 0%, #F5F0EB 50%, #EDE7E0 100%);
  z-index: -3;
}

/* 光晕装饰 */
.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.5;
  z-index: -2;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #FFE8D6, #FFD4B8);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #E8F5E9, #C8E6C9);
  bottom: -100px;
  left: -100px;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.3;
}

/* 内容包装 */
.content-wrapper {
  display: flex;
  align-items: stretch;
  padding: 0;
  width: 100%;
  min-height: 100vh;
}

.sidebar {
  width: 280px;
  flex-shrink: 0;
  height: 100vh;
  overflow-y: auto;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 2;
}

.nav-card {
  min-height: 100%;
  padding: 0 0 20px;
  display: flex;
  flex-direction: column;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 48px;
  height: 48px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.nav-logo .brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 2px;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px 12px 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.category-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  overflow-y: auto;
}

.mobile-topbar {
  display: none;
}

.mobile-topbar-offset {
  display: none;
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.mobile-topbar-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0 12px;
  color: #2d3436;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-topbar-spacer {
  display: none;
}

/* 卡片容器 */
.cards-container {
  display: flex;
  gap: 48px;
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.cards-column {
  width: min(100%, 920px);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 36px;
}

.cards-column .selection-card {
  width: 100%;
  max-width: none;
}

/* 玻璃卡片 */
.glass-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.selection-card {
  flex: 1;
  max-width: 420px;
  padding: 22px 26px;
  display: flex;
  flex-direction: column;
}

.selection-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.selection-card.active {
  transform: translateY(-8px) scale(1.02);
  box-shadow:
    0 20px 60px rgba(255, 138, 61, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.selection-card:active {
  transform: translateY(-4px) scale(1.01);
}

/* 卡片内发光效果 */
.card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.6s ease;
}

.selection-card:hover .card-shine {
  left: 100%;
}

/* 卡片内部 */
.card-inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px;
  grid-template-areas:
    "tag tag"
    "title icon"
    "desc icon";
  column-gap: 16px;
  row-gap: 6px;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  height: 100%;
  min-height: 132px;
}

/* 标签 */
.card-tag {
  grid-area: tag;
  display: inline-block;
  width: fit-content;
  background: #FF8A3D;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  padding: 7px 18px;
  border-radius: 20px;
  letter-spacing: 1px;
}

.card-tag.song-tag {
  background: linear-gradient(135deg, #4CAF50, #66BB6A);
  color: #FFFFFF;
}

.card-tag.tpr-tag {
  background: linear-gradient(135deg, #20b2aa, #4dd0c8);
  color: #FFFFFF;
}

.card-tag.audio-tag {
  background: linear-gradient(135deg, #ff9f43, #ff7f50);
  color: #FFFFFF;
}

.card-tag.flash-tag {
  background: linear-gradient(135deg, #5B8FF9, #7B68EE);
  color: #FFFFFF;
}

.card-tag.picture-tag {
  background: linear-gradient(135deg, #9C6ADE, #C084FC);
  color: #FFFFFF;
}

/* 图标 */
.card-icon {
  grid-area: icon;
  align-self: center;
  justify-self: end;
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  animation: breathe 3s ease-in-out infinite;
}

.tpr-icon {
  background: linear-gradient(135deg, #e6fffb, #c8f7f2);
  color: #1fa79d;
  box-shadow: 0 8px 32px rgba(32, 178, 170, 0.24);
}

.song-icon {
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  color: #5B8FF9;
  box-shadow: 0 8px 32px rgba(91, 143, 249, 0.25);
}

.audio-icon {
  background: linear-gradient(135deg, #FFF1E8, #FFD8BF);
  color: #FF8A3D;
  box-shadow: 0 8px 32px rgba(255, 138, 61, 0.24);
}

.flash-icon {
  background: linear-gradient(135deg, #EEF4FF, #E7E3FF);
  color: #6B5FF6;
  box-shadow: 0 8px 32px rgba(107, 95, 246, 0.22);
}

.picture-icon {
  background: linear-gradient(135deg, #F1E8FF, #E9D5FF);
  color: #9C6ADE;
  box-shadow: 0 8px 32px rgba(156, 106, 222, 0.22);
}

/* 标题 */
.card-title {
  grid-area: title;
  align-self: center;
  font-size: 28px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  margin: 0;
}

/* 描述 */
.card-desc {
  grid-area: desc;
  font-size: 14px;
  color: #666;
  line-height: 1.45;
  padding-right: 0;
}

.toast-message {
  position: fixed;
  left: 50%;
  top: 50%;
  z-index: 50;
  max-width: calc(100vw - 40px);
  padding: 12px 20px;
  border-radius: 999px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  background: rgba(45, 52, 54, 0.92);
  box-shadow: 0 12px 32px rgba(45, 52, 54, 0.18);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% + 8px));
}

/* 动画 */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式 */
@media (max-width: 900px) {
  .sidebar {
    width: 236px;
  }

  .nav-logo {
    padding: 20px 16px;
    gap: 10px;
  }

  .nav-logo .logo-icon {
    width: 42px;
    height: 42px;
    font-size: 20px;
  }

  .nav-logo .brand-name {
    font-size: 18px;
  }

  .nav-item {
    padding: 16px;
  }

  .category-main {
    padding: 32px 24px;
  }

  .cards-container {
    align-items: center;
    gap: 24px;
  }

  .cards-column {
    width: 100%;
    max-width: 820px;
  }

  .selection-card {
    width: 100%;
    padding: 22px 24px;
  }

  .card-inner {
    grid-template-columns: minmax(0, 1fr) 64px;
    min-height: 124px;
  }

  .card-title {
    font-size: 24px;
  }

  .card-icon {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .login-page {
    align-items: flex-start;
    justify-content: flex-start;
    overflow-y: auto;
  }

  .content-wrapper {
    min-height: 100vh;
  }

  .sidebar {
    display: none;
  }

  .category-main {
    padding: 30px 16px 0;
    align-items: stretch;
    justify-content: flex-start;
    width: 100%;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .cards-column {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .category-main {
    padding: 24px 14px 0;
  }

  .mobile-topbar {
    padding-left: 14px;
    padding-right: 14px;
  }

  .mobile-back-btn {
    left: 14px;
  }

  .selection-card {
    padding: 18px 20px;
  }

  .card-inner {
    grid-template-columns: minmax(0, 1fr) 56px;
    min-height: 112px;
  }

  .card-title {
    font-size: 22px;
  }

  .card-tag {
    font-size: 12px;
    padding: 6px 15px;
  }

  .card-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    font-size: 24px;
  }

  .card-desc {
    font-size: 13px;
    line-height: 1.4;
  }
}
</style>
